import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

type AlertType = 'success' | 'error' | 'warning' | 'info';

interface Alert {
  id: number;
  message: string;
  type: AlertType;
}

interface AlertContextType {
  showAlert: (message: string, type?: AlertType) => void;
}

const AlertContext = createContext<AlertContextType | null>(null);

export const useAlert = (): AlertContextType => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

const ALERT_STYLES: Record<AlertType, { bg: string; border: string; text: string; icon: string }> = {
  success: { bg: 'bg-green-50', border: 'border-green-400', text: 'text-green-800', icon: '✓' },
  error: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-800', icon: '!' },
  warning: { bg: 'bg-yellow-50', border: 'border-yellow-400', text: 'text-yellow-800', icon: '⚠' },
  info: { bg: 'bg-blue-50', border: 'border-blue-400', text: 'text-blue-800', icon: 'i' },
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const idRef = useRef(0);

  const showAlert = useCallback((message: string, type: AlertType = 'info') => {
    const id = ++idRef.current;
    setAlerts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setAlerts(prev => prev.filter(a => a.id !== id));
    }, 5000);
  }, []);

  const dismiss = useCallback((id: number) => {
    setAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  return (
    <AlertContext.Provider value={{ showAlert }}>
      {children}
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 420 }}>
        {alerts.map(alert => {
          const style = ALERT_STYLES[alert.type];
          return (
            <div
              key={alert.id}
              className={`${style.bg} ${style.text} border-l-4 ${style.border} px-4 py-3 rounded shadow-lg flex items-start gap-3 animate-slide-in`}
              role="alert"
            >
              <span className="font-bold text-lg leading-none mt-0.5">{style.icon}</span>
              <p className="text-sm flex-1">{alert.message}</p>
              <button
                onClick={() => dismiss(alert.id)}
                className={`${style.text} hover:opacity-70 font-bold text-lg leading-none ml-2`}
                aria-label="Dismiss"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    </AlertContext.Provider>
  );
};
